import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { images } from '../utils/images';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    interests: ''
  });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/send-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          interests: formData.interests,
          honeypot: '',
        }),
      });

      if (!res.ok) throw new Error('Failed to send');

      toast.success("Thank you for contacting us! We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', phone: '', message: '', interests: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact"
      ref={sectionRef}
      className={`py-20 bg-white transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
            style={{ color: '#587740' }}
          >
            Get In Touch
          </h2>
          <p 
            className="text-lg md:text-xl"
            style={{ color: '#5e5e5e' }}
          >
            Ready to take the next step? Contact us today and let's discuss how we can 
            support you on your journey.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 
                className="text-2xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Contact Information
              </h3>
              <p 
                className="text-base leading-relaxed mb-8"
                style={{ color: '#5e5e5e' }}
              >
                We're here to answer your questions and help you get started with the support 
                you need. Reach out to us through any of the following channels.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#AABC5A' }}
                >
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 
                    className="font-semibold text-lg mb-1"
                    style={{ color: '#587740' }}
                  >
                    Phone
                  </h4>
                  <a 
                    href="tel:+611300404229"
                    className="text-base hover:underline"
                    style={{ color: '#5e5e5e' }}
                  >
                    1300 404 229
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#7A993B' }}
                >
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 
                    className="font-semibold text-lg mb-1"
                    style={{ color: '#587740' }}
                  >
                    Email
                  </h4>
                  <a 
                    href="mailto:admin@lifecarewellness.au"
                    className="text-base hover:underline"
                    style={{ color: '#5e5e5e' }}
                  >
                    admin@lifecarewellness.au
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#6c935e' }}
                >
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 
                    className="font-semibold text-lg mb-1"
                    style={{ color: '#587740' }}
                  >
                    Location
                  </h4>
                  <p 
                    className="text-base"
                    style={{ color: '#5e5e5e' }}
                  >
                    Serving Logan & Brisbane, Queensland
                  </p>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="rounded-xl overflow-hidden shadow-lg mt-8">
              <img 
                src={images.contact}
                alt="Life Care and Wellness NDIS support team activities"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-none shadow-xl">
            <CardHeader>
              <CardTitle 
                className="text-2xl"
                style={{ color: '#587740' }}
              >
                Send Us a Message
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" style={{ color: '#587740' }}>
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2 border-gray-300 focus:border-[#81b956] focus:ring-[#81b956]"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" style={{ color: '#587740' }}>
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2 border-gray-300 focus:border-[#81b956] focus:ring-[#81b956]"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" style={{ color: '#587740' }}>
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2 border-gray-300 focus:border-[#81b956] focus:ring-[#81b956]"
                    placeholder="04XX XXX XXX"
                  />
                </div>

                <div>
                  <Label htmlFor="message" style={{ color: '#587740' }}>
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="mt-2 border-gray-300 focus:border-[#81b956] focus:ring-[#81b956]"
                    placeholder="Tell us how we can help you..."
                  />
                </div>

                <div>
                  <Label htmlFor="interests" style={{ color: '#587740' }}>
                    Your Individual Interests
                  </Label>
                  <Textarea
                    id="interests"
                    name="interests"
                    value={formData.interests}
                    onChange={handleChange}
                    rows={3}
                    className="mt-2 border-gray-300 focus:border-[#81b956] focus:ring-[#81b956]"
                    placeholder="Tell us about your hobbies, passions, and activities you enjoy..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#81b956' }}
                  size="lg"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
